import React from 'react';
import { skillRegistry } from './SkillRegistry';
import { McpSkillSetup } from '../screens/skill-setup/McpSkillSetup';
import { DrataSkillSetup } from '../screens/skill-setup/DrataSkillSetup';
import { VantaSkillSetup } from '../screens/skill-setup/VantaSkillSetup';
import { ServiceNowSkillSetup } from '../screens/skill-setup/ServiceNowSkillSetup';
import { JiraSkillSetup } from '../screens/skill-setup/JiraSkillSetup';
import { ClickUpSkillSetup } from '../screens/skill-setup/ClickUpSkillSetup';
import { LinearSkillSetup } from '../screens/skill-setup/LinearSkillSetup';
import { PlerionSkillSetup } from '../screens/skill-setup/PlerionSkillSetup';
import { GitHubSkillSetup } from '../screens/skill-setup/GitHubSkillSetup';
import { GitLabSkillSetup } from '../screens/skill-setup/GitLabSkillSetup';
import { AwseyeSkillSetup } from '../screens/skill-setup/AwseyeSkillSetup';
import { AwsSecurityDigestSkillSetup } from '../screens/skill-setup/AwsSecurityDigestSkillSetup';
import { CloudNewsSkillSetup } from '../screens/skill-setup/CloudNewsSkillSetup';
import { Image } from '../../ui/Image';
import { BitbucketSkillSetup } from '../screens/skill-setup/BitbucketSkillSetup';

// Noop setup component for always-enabled skills
const NoopSkillSetup: React.FC = () => null;

// Register skills
skillRegistry.register(
  {
    id: 'plerion',
    name: 'Plerion',
    description:
      'Allows me to identify and prioritize the 1% of risks that matter.',
    icon: <Image type="plerionLogo" name="logo-small" width={20} height={20} />,
    image: {
      name: 'logo-large',
      type: 'plerionLogo',
      className: '!w-auto',
    },
    tags: ['security', 'compliance'],
    category: 'Cloud',
    configurable: false,
    status: 'available',
    alwaysEnabledForOrgs: ['*'],
  },
  PlerionSkillSetup,
);
// GitHub Skill
skillRegistry.register(
  {
    id: 'github',
    name: 'GitHub',
    description:
      'Allows me to search and fix issues in your GitHub repositories.',
    icon: (
      <Image
        src="resourceType/github-secret.png"
        alt="GitHub"
        width={20}
        height={20}
      />
    ),
    tags: ['security', 'development'],
    category: 'Code',
    configurable: true,
    status: 'available',
    image: {
      name: 'github-secret',
      type: 'resourceType',
      className: '!w-auto',
    },
  },
  GitHubSkillSetup,
);

skillRegistry.register(
  {
    id: 'gitlab',
    name: 'GitLab',
    description:
      'Allows me to search and fix issues in your GitLab repositories.',
    icon: (
      <Image
        src="resourceType/gitlab-secret.png"
        alt="GitLab"
        width={20}
        height={20}
      />
    ),
    tags: ['security', 'development'],
    category: 'Code',
    configurable: true,
    status: 'available',
    image: {
      name: 'gitlab-secret',
      type: 'resourceType',
      className: '!w-auto',
    },
  },
  GitLabSkillSetup,
);

skillRegistry.register(
  {
    id: 'bitbucket',
    name: 'Bitbucket',
    description:
      'Allows me to search and fix issues in your Bitbucket repositories.',
    icon: (
      <Image
        src="resourceType/bitbucket-secret.png"
        alt="Bitbucket"
        width={20}
        height={20}
      />
    ),
    tags: ['security', 'development'],
    category: 'Code',
    configurable: true,
    status: 'available',
    image: {
      name: 'bitbucket-secret',
      type: 'resourceType',
      className: '!w-auto',
    },
  },
  BitbucketSkillSetup,
);
// AWS Skill
skillRegistry.register(
  {
    id: 'aws',
    name: 'AWS',
    description:
      'Allows me to access your AWS resources for live security insights.',
    icon: '🔍',
    tags: ['security', 'cloud'],
    category: 'Cloud',
    configurable: true,
    status: 'hidden',
    image: {
      name: 'aws',
      type: 'provider',
      className: '!w-auto',
    },
  },
  NoopSkillSetup,
);
// Drata Skill (always enabled for demo/dev/main orgs)
skillRegistry.register(
  {
    id: 'drata',
    name: 'Drata',
    description:
      'Allows me to provide compliance and security insights from Drata.',
    icon: <Image src="logos/drata.svg" alt="Drata" width={20} height={20} />,
    tags: ['compliance', 'security'],
    category: 'Compliance',
    configurable: true,
    status: 'available',
    alwaysEnabledForOrgs: [],
    image: {
      src: 'logos/drata.svg',
      alt: 'Drata',
    },
  },
  DrataSkillSetup,
);

// Vanta Skill (always enabled for demo/dev/main orgs)
skillRegistry.register(
  {
    id: 'vanta',
    name: 'Vanta',
    description:
      'Allows me to provide compliance and security insights from Vanta.',
    icon: <Image src="logos/vanta.png" alt="Vanta" width={20} height={20} />,
    tags: ['compliance', 'security'],
    category: 'Compliance',
    configurable: true,
    status: 'available',
    image: {
      src: 'logos/vanta.png',
      alt: 'Vanta',
    },
  },
  VantaSkillSetup,
);

// ServiceNow Skill
skillRegistry.register(
  {
    id: 'ServiceNow',
    name: 'ServiceNow',
    description:
      'Allows me to manage and track incidents in your ServiceNow instance.',
    icon: (
      <Image
        src="logos/service-now.png"
        alt="ServiceNow"
        width={20}
        height={20}
      />
    ),
    tags: ['security', 'ticketing'],
    category: 'Ticketing',
    configurable: true,
    status: 'available',
    image: {
      src: 'logos/service-now.png',
      alt: 'ServiceNow',
    },
  },
  ServiceNowSkillSetup,
);

skillRegistry.register(
  {
    id: 'aws-eye',
    name: 'Awseye',
    description: 'Allows me to track and analyze publicly accessible AWS data.',
    icon: '🔍',
    tags: ['security', 'intelligence'],
    category: 'Cloud',
    configurable: false,
    status: 'available',
    alwaysEnabledForOrgs: ['*'],
    image: {
      name: 'aws-eye',
      type: 'resourceType',
      fileExtension: 'gif',
      className: '!w-auto',
    },
  },
  AwseyeSkillSetup,
);

// AWS security digest Skill (always enabled for demo/dev/main orgs)
skillRegistry.register(
  {
    id: 'awssecuritydigest',
    name: 'AWS security digest',
    description: 'Allows me to deliver curated AWS security news and updates.',
    icon: (
      <Image
        src="logos/awssecuritydigest.png"
        alt="AWS security digest"
        width={20}
        height={20}
      />
    ),
    tags: ['news', 'security'],
    category: 'Cloud',
    configurable: false,
    status: 'available',
    alwaysEnabledForOrgs: ['*'],
    image: {
      src: 'logos/awssecuritydigest.png',
      alt: 'AWS security digest',
    },
  },
  AwsSecurityDigestSkillSetup,
);
// Cloud news Skill (always enabled for demo/dev/main orgs)
skillRegistry.register(
  {
    id: 'cloudNews',
    name: 'Cloud news',
    description:
      'Allows me to deliver the latest news from AWS, Azure, and GCP.',
    icon: (
      <Image
        src="logos/cloud-news.png"
        alt="Cloud news"
        width={20}
        height={20}
      />
    ),
    tags: ['news', 'communication'],
    category: 'Cloud',
    configurable: false,
    status: 'available',
    alwaysEnabledForOrgs: ['*'],
    image: {
      src: 'logos/cloud-news.png',
      alt: 'Cloud news',
    },
  },
  CloudNewsSkillSetup,
);

// MCP Skill
skillRegistry.register(
  {
    id: 'mcp',
    name: 'Model Context Protocol',
    description:
      'Allows me to extend my capabilities with your custom tools and APIs.',
    icon: '🔌',
    tags: ['development', 'integration'],
    category: 'Messaging',
    configurable: true,
    status: 'hidden',
    allowMultipleInstances: true,
    image: {
      name: 'mcp',
      type: 'resourceType',
      className: '!w-auto',
    },
  },
  McpSkillSetup,
);

// Jira Skill
skillRegistry.register(
  {
    id: 'jira',
    name: 'Jira',
    description: 'Allows me to search and manage issues in your Jira projects.',
    icon: <Image src="logos/jira.svg" alt="Jira" width={20} height={20} />,
    tags: ['project-management', 'productivity'],
    category: 'Ticketing',
    configurable: true,
    status: 'available',
    image: {
      src: 'logos/jira.svg',
      alt: 'Jira',
      className: '!w-auto',
    },
  },
  JiraSkillSetup,
);

// ClickUp Skill
skillRegistry.register(
  {
    id: 'clickup',
    name: 'ClickUp',
    description:
      'Allows me to create and manage tasks in your ClickUp lists to help track and organize your work.',
    icon: (
      <Image src="logos/clickup.png" alt="ClickUp" width={20} height={20} />
    ),
    tags: ['project-management', 'productivity'],
    category: 'Ticketing',
    configurable: true,
    status: 'available',
    image: {
      src: 'logos/clickup.png',
      alt: 'ClickUp',
    },
  },
  ClickUpSkillSetup,
);

// Linear Skill
skillRegistry.register(
  {
    id: 'linear',
    name: 'Linear',
    description:
      'Allows me to create and manage issues in your Linear workspace to help track and organize your security and compliance work.',
    icon: <Image src="logos/linear.png" alt="Linear" width={20} height={20} />,
    tags: ['project-management', 'productivity'],
    category: 'Ticketing',
    configurable: true,
    status: 'available',
    image: {
      src: 'logos/linear.png',
      alt: 'Linear',
      className: '!w-auto',
    },
  },
  LinearSkillSetup,
);
